import React from 'react';
import PropTypes from 'prop-types';
import {injectIntl} from 'react-intl';
import {ICONS} from '../../lib/curo-icons.js';
import {saveProject, loadSaved, clearSaved, summarize} from '../../lib/curo-autosave.js';

/**
 * 블록 프로젝트 자동저장 + 이어하기 — 크래시·탭닫힘·새로고침에 수업 40분을 지킨다.
 * 2026-07-23 신설(갭 점검 1순위): 파이썬 코드는 자동저장되는데 블록은 안 되던 구멍.
 * ⭐ 전부 기기(IndexedDB)에만. 서버 전송 0.
 * 저장 주기 = 실측 적응형(느린 PC에서 저장이 오래 걸리면 간격을 늘림 — AI탭과 같은 방식).
 * 안전장치: ①빈 프로젝트(블록 0)는 저장 안 함 = 지난 저장을 빈 것으로 덮어쓰지 않음
 *          ②이미 작업 중(블록>0)이면 이어하기 배너 안 띄움 = 남의 작업 덮지 않음
 */

const MSG = {
    title: {en: 'Resume your work?', ko: '이어서 할까요?', vi: 'Tiếp tục công việc?'},
    body: {en: 'We found your last project', ko: '지난 작업이 저장되어 있어요', vi: 'Đã tìm thấy dự án lần trước'},
    blocksU: {en: 'blocks', ko: '블록', vi: 'khối'},
    spritesU: {en: 'sprites', ko: '스프라이트', vi: 'nhân vật'},
    resume: {en: 'Resume', ko: '이어하기', vi: 'Tiếp tục'},
    fresh: {en: 'Start fresh', ko: '새로 시작', vi: 'Bắt đầu mới'},
    saved: {en: 'Autosaved', ko: '자동저장됨', vi: 'Đã tự động lưu'}
};
const MSG_EXTRA = {
    ja: {title: '続きから始めますか？', body: '前回の作業が保存されています', blocksU: 'ブロック', spritesU: 'スプライト', resume: '続きから', fresh: '新しく始める', saved: '自動保存しました'},
    'zh-cn': {title: '继续上次的作品吗？', body: '找到了上次保存的作品', blocksU: '积木', spritesU: '角色', resume: '继续', fresh: '重新开始', saved: '已自动保存'},
    'zh-tw': {title: '繼續上次的作品嗎？', body: '找到了上次儲存的作品', blocksU: '積木', spritesU: '角色', resume: '繼續', fresh: '重新開始', saved: '已自動儲存'},
    de: {title: 'Weitermachen?', body: 'Dein letztes Projekt wurde gefunden', blocksU: 'Blöcke', spritesU: 'Figuren', resume: 'Weiter', fresh: 'Neu starten', saved: 'Automatisch gespeichert'},
    es: {title: '¿Continuar tu trabajo?', body: 'Encontramos tu último proyecto', blocksU: 'bloques', spritesU: 'objetos', resume: 'Continuar', fresh: 'Empezar de nuevo', saved: 'Guardado automático'},
    fr: {title: 'Reprendre ton travail ?', body: 'Nous avons trouvé ton dernier projet', blocksU: 'blocs', spritesU: 'lutins', resume: 'Reprendre', fresh: 'Recommencer', saved: 'Sauvegarde auto'},
    it: {title: 'Riprendere il lavoro?', body: 'Abbiamo trovato il tuo ultimo progetto', blocksU: 'blocchi', spritesU: 'sprite', resume: 'Riprendi', fresh: 'Ricomincia', saved: 'Salvato automaticamente'},
    ru: {title: 'Продолжить работу?', body: 'Мы нашли ваш последний проект', blocksU: 'блоков', spritesU: 'спрайтов', resume: 'Продолжить', fresh: 'Начать заново', saved: 'Автосохранено'}
};

const FAST_MS = 20000;   // 기본 저장 간격
const SLOW_MS = 60000;   // 저장이 1.5s+ 걸리는 PC
const SLOWEST_MS = 120000; // 4s+

class Autosave extends React.Component {
    constructor (props) {
        super(props);
        this.state = {resume: null, toast: null};
        this._dirty = false;
        this._saving = false;
        this._interval = FAST_MS;
        this._mounted = false;
        this._tick = this._tick.bind(this);
        this._onChanged = this._onChanged.bind(this);
        this._doResume = this._doResume.bind(this);
        this._doFresh = this._doFresh.bind(this);
    }
    componentDidMount () {
        this._mounted = true;
        const vm = this.props.vm;
        if (!vm) return;
        try { vm.on('PROJECT_CHANGED', this._onChanged); } catch (e) { /* noop */ }
        // 초기 프로젝트 로딩이 끝난 뒤에 판단(이미 작업 중이면 배너 안 띄움)
        this._bootId = setTimeout(() => {
            let cur = {blocks: 0};
            try { cur = summarize(vm); } catch (e) { /* noop */ }
            if (cur.blocks > 0) return; // 이미 뭔가 있음 → 건드리지 않음
            // 시작 화면(첫 방문 선택 UI)이 떠 있으면 배너를 겹치지 않게 미룸.
            // curoai_start_seen 이 없으면 = 시작 화면이 뜰 차례 → 사용자가 고르고 나서 다음 기회에.
            try {
                if (!localStorage.getItem('curoai_start_seen')) return;
            } catch (e) { /* noop */ }
            loadSaved().then(rec => {
                if (this._mounted && rec && rec.blocks > 0) this.setState({resume: rec});
            }).catch(() => { /* noop */ });
        }, 1500);
        this._schedule();
    }
    componentWillUnmount () {
        this._mounted = false;
        try { this.props.vm.off('PROJECT_CHANGED', this._onChanged); } catch (e) { /* noop */ }
        clearTimeout(this._timerId);
        clearTimeout(this._bootId);
        clearTimeout(this._toastId);
    }
    _onChanged () { this._dirty = true; }
    _schedule () {
        clearTimeout(this._timerId);
        this._timerId = setTimeout(this._tick, this._interval);
    }
    _tick () {
        if (!this._mounted) return;
        if (!this._dirty || this._saving) { this._schedule(); return; }
        let cur = {blocks: 0};
        try { cur = summarize(this.props.vm); } catch (e) { /* noop */ }
        // 빈 프로젝트는 저장 안 함 — 지난 저장을 빈 것으로 덮어쓰지 않기 위해
        if (cur.blocks === 0) { this._dirty = false; this._schedule(); return; }
        this._dirty = false;
        this._saving = true;
        const t0 = Date.now();
        saveProject(this.props.vm).then(info => {
            const ms = Date.now() - t0;
            this._interval = ms > 4000 ? SLOWEST_MS : (ms > 1500 ? SLOW_MS : FAST_MS);
            this._saving = false;
            if (!this._mounted) return;
            this.setState({toast: {at: Date.now(), blocks: info.blocks}});
            clearTimeout(this._toastId);
            this._toastId = setTimeout(() => { if (this._mounted) this.setState({toast: null}); }, 2600);
            this._schedule();
        }).catch(() => {
            this._saving = false;
            this._schedule();
        });
    }
    _doResume () {
        const rec = this.state.resume;
        this.setState({resume: null});
        if (!rec || !rec.buf) return;
        try {
            const p = this.props.vm.loadProject(rec.buf);
            if (p && p.catch) p.catch(() => { /* noop */ });
        } catch (e) { /* noop */ }
    }
    _doFresh () {
        this.setState({resume: null});
        clearSaved().catch(() => { /* noop */ });
    }
    t (key) {
        let loc = (this.props.intl && this.props.intl.locale) || 'en';
        if (loc === 'ja-Hira') loc = 'ja';
        const ex = MSG_EXTRA[loc];
        if (ex && ex[key] != null) return ex[key];
        const m = MSG[key];
        if (!m) return key;
        return m[loc] || m.en || key;
    }
    _when (ts) {
        const d = new Date(ts);
        const pad = n => `0${n}`.slice(-2);
        const hm = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
        const now = new Date();
        const sameDay = d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
        return sameDay ? hm : `${d.getMonth() + 1}/${d.getDate()} ${hm}`;
    }
    render () {
        const {resume, toast} = this.state;
        if (resume) {
            return (
                <div style={S.card}>
                    <div style={S.row}>
                        <span style={S.icon} dangerouslySetInnerHTML={{__html: ICONS.disk}} />
                        <div style={{minWidth: 0}}>
                            <div style={S.title}>{this.t('title')}</div>
                            <div style={S.body}>
                                {this.t('body')}
                                <span style={S.dim}>{` · ${this._when(resume.savedAt)}`}</span>
                            </div>
                            <div style={S.meta}>
                                {`${resume.blocks} ${this.t('blocksU')} · ${resume.sprites} ${this.t('spritesU')}`}
                            </div>
                        </div>
                    </div>
                    <div style={S.btnRow}>
                        <button style={S.primary} onClick={this._doResume}>{this.t('resume')}</button>
                        <button style={S.ghost} onClick={this._doFresh}>{this.t('fresh')}</button>
                    </div>
                </div>
            );
        }
        if (toast) {
            return (
                <div style={S.toast}>
                    <span style={S.toastIcon} dangerouslySetInnerHTML={{__html: ICONS.disk}} />
                    {`${this.t('saved')} ${this._when(toast.at)}`}
                </div>
            );
        }
        return null;
    }
}

const S = {
    card: {position: 'fixed', left: 16, bottom: 16, zIndex: 9000, background: '#fff', borderRadius: 12,
        boxShadow: '0 8px 28px rgba(0,0,0,0.22)', border: '1px solid #e0e3ec', padding: '12px 14px',
        width: 288, fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif'},
    row: {display: 'flex', gap: 10, alignItems: 'flex-start'},
    icon: {display: 'inline-flex', fontSize: 20, color: '#14c8a6', flex: 'none', marginTop: 1},
    title: {fontSize: 14, fontWeight: 'bold', color: '#1f2540'},
    body: {fontSize: 12, color: '#67708a', marginTop: 2},
    dim: {color: '#9aa1b4'},
    meta: {fontSize: 11, color: '#9aa1b4', marginTop: 3},
    btnRow: {display: 'flex', gap: 6, marginTop: 10, justifyContent: 'flex-end'},
    primary: {border: 'none', borderRadius: 16, background: '#14c8a6', color: '#fff', fontSize: 12,
        padding: '6px 14px', cursor: 'pointer'},
    ghost: {border: 'none', borderRadius: 16, background: '#eef0f5', color: '#4a5169', fontSize: 12,
        padding: '6px 12px', cursor: 'pointer'},
    toast: {position: 'fixed', left: 16, bottom: 16, zIndex: 9000, background: 'rgba(31,37,64,0.88)',
        color: '#fff', borderRadius: 16, padding: '6px 13px', fontSize: 12, display: 'flex',
        alignItems: 'center', gap: 6, fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif'},
    toastIcon: {display: 'inline-flex', fontSize: 14, color: '#7ef0d8'}
};

Autosave.propTypes = {
    intl: PropTypes.object,
    vm: PropTypes.object
};

export default injectIntl(Autosave);
